import { lazy, isValidElement, type ComponentType } from 'react'
import { matchRoutes, type RouteObject } from 'react-router-dom'
import { stripLocale } from './locale'

const RELOAD_KEY = 'chunk-reload-at'
const RELOAD_WINDOW_MS = 60_000

// After a deploy, an open tab asks for chunk files that no longer exist. One hard
// reload picks up the new index.html; a second failure within a minute is a real
// error and goes to the error boundary instead of looping.
export function reloadForStaleChunk(error: unknown): Promise<never> {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (Date.now() - last > RELOAD_WINDOW_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
      window.location.reload()
      return new Promise<never>(() => {})
    }
  } catch {
    /* no sessionStorage, no loop guard: do not reload */
  }
  return Promise.reject(error)
}

type Loader<T> = () => Promise<{ default: T }>

export function lazyPage<T extends ComponentType<any>>(load: Loader<T>) {
  let pending: Promise<{ default: T }> | undefined
  const fetchOnce = () => (pending ??= load().catch((error: unknown) => {
    pending = undefined
    throw error
  }))
  const Page = lazy(() => fetchOnce().catch(reloadForStaleChunk))
  return Object.assign(Page, { load: fetchOnce, preload: () => fetchOnce().then(() => undefined, () => undefined) })
}

let routes: RouteObject[] = []

export function registerRoutes(next: RouteObject[]) {
  routes = next
}

export function registeredRoutes() {
  return routes
}

function preloadOf(element: unknown): (() => Promise<void>) | undefined {
  if (!isValidElement(element)) return undefined
  return (element.type as { preload?: () => Promise<void> }).preload
}

const preloaded = new Set<string>()

// Warms the chunks of every page the path would render, so a click finds them ready.
export function preloadPath(href: string) {
  const url = new URL(href, window.location.origin)
  if (url.origin !== window.location.origin || preloaded.has(url.pathname)) return
  preloaded.add(url.pathname)
  for (const match of matchRoutes(routes, stripLocale(url.pathname)) ?? []) {
    void preloadOf(match.route.element)?.()
  }
}
